import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { BlogPost } from '@/types/blog';
import PostsList from './components/PostsList';
import PostForm from './components/PostForm';

type View = 'list' | 'create' | 'edit';

export default function AdminPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<BlogPost | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('data_publicacao', { ascending: false });

    if (!error && data) {
      setPosts(data as BlogPost[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }
      setAdminEmail(session.user.email ?? '');
      fetchPosts();
    };
    checkAuth();
  }, [navigate, fetchPosts]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setView('edit');
  };

  const handleCreate = () => {
    setEditingPost(null);
    setView('create');
  };

  const handleSaved = () => {
    setView('list');
    setEditingPost(null);
    fetchPosts();
    setSuccessMsg(editingPost ? 'Post atualizado com sucesso!' : 'Post criado com sucesso!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleCancel = () => {
    setView('list');
    setEditingPost(null);
  };

  const handleTogglePublish = async (post: BlogPost) => {
    const { error } = await supabase
      .from('blog_posts')
      .update({ publicado: !post.publicado })
      .eq('id', post.id);

    if (!error) {
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, publicado: !post.publicado } : p))
      );
      setSuccessMsg(post.publicado ? 'Post despublicado.' : 'Post publicado com sucesso!');
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);

    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', deleteConfirm.id);

    setDeleting(false);
    setDeleteConfirm(null);

    if (!error) {
      setPosts((prev) => prev.filter((p) => p.id !== deleteConfirm.id));
      setSuccessMsg('Post excluído com sucesso.');
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  };

  const publishedCount = posts.filter((p) => p.publicado).length;
  const draftCount = posts.filter((p) => !p.publicado).length;

  return (
    <main className="min-h-screen bg-[#F8F5EE] font-sans">
      {/* Header */}
      <header className="bg-[#0A1829] border-b border-white/10 px-6 lg:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-[#B39B62] text-xs font-semibold tracking-[0.2em] uppercase">Painel</p>
            <h1 className="text-white font-serif text-lg font-light leading-tight" style={{ fontFamily: '\'Cormorant Garamond\', serif' }}>
              Moreira Tófoli Advogados
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 text-white/40">
            <i className="ri-user-line text-sm"></i>
            <span className="font-sans text-xs">{adminEmail}</span>
          </div>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate('/blog'); }}
            className="hidden sm:inline-flex items-center gap-2 text-white/50 hover:text-white font-sans text-xs border border-white/15 hover:border-white/30 px-3 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line text-sm"></i>
            Ver blog
          </a>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-white/50 hover:text-white font-sans text-xs border border-white/15 hover:border-white/30 px-3 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {/* Success message */}
        {successMsg && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md px-4 py-3 flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-green-500 text-base"></i>
            <p className="font-sans text-sm text-green-700">{successMsg}</p>
          </div>
        )}

        {/* Stats */}
        {view === 'list' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { label: 'Total de posts', value: posts.length, icon: 'ri-article-line' },
              { label: 'Publicados', value: publishedCount, icon: 'ri-eye-line' },
              { label: 'Rascunhos', value: draftCount, icon: 'ri-draft-line' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white border border-[#E9D59C]/40 rounded-lg p-5 flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#B39B62]/10 border border-[#B39B62]/20 shrink-0">
                  <i className={`${stat.icon} text-[#B39B62] text-base`}></i>
                </div>
                <div>
                  <p className="font-sans text-2xl font-light text-[#0A1829]">{stat.value}</p>
                  <p className="font-sans text-xs text-[#0A1829]/45">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Main content */}
        <div className="bg-white border border-[#E9D59C]/40 rounded-lg overflow-hidden">
          {/* Section header */}
          <div className="px-6 py-5 border-b border-[#E9D59C]/40 flex items-center justify-between">
            <div>
              {view === 'list' && (
                <>
                  <h2 className="font-serif text-xl text-[#0A1829] font-medium">Posts do Blog</h2>
                  <p className="font-sans text-xs text-[#0A1829]/45 mt-0.5">Gerencie todos os artigos do escritório</p>
                </>
              )}
              {view === 'create' && (
                <>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 text-[#B39B62] font-sans text-xs font-semibold uppercase tracking-wider hover:gap-3 transition-all cursor-pointer mb-2"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Voltar
                  </button>
                  <h2 className="font-serif text-xl text-[#0A1829] font-medium">Novo Post</h2>
                </>
              )}
              {view === 'edit' && (
                <>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 text-[#B39B62] font-sans text-xs font-semibold uppercase tracking-wider hover:gap-3 transition-all cursor-pointer mb-2"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Voltar
                  </button>
                  <h2 className="font-serif text-xl text-[#0A1829] font-medium">Editar Post</h2>
                </>
              )}
            </div>
            {view === 'list' && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 bg-[#0A1829] hover:bg-[#0A1829]/85 text-white font-sans font-semibold text-sm px-5 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Novo post
              </button>
            )}
          </div>

          <div className="p-6">
            {view === 'list' && (
              <PostsList
                posts={posts}
                loading={loading}
                onEdit={handleEdit}
                onDelete={setDeleteConfirm}
                onTogglePublish={handleTogglePublish}
              />
            )}
            {(view === 'create' || view === 'edit') && (
              <PostForm
                post={editingPost}
                onSaved={handleSaved}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(10,24,41,0.6)' }}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full border border-[#E9D59C]/40">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 border border-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="font-serif text-lg text-[#0A1829] font-medium text-center mb-1">Excluir post?</h3>
            <p className="font-sans text-sm text-[#0A1829]/55 text-center mb-1 line-clamp-2">{deleteConfirm.titulo}</p>
            <p className="font-sans text-xs text-[#0A1829]/40 text-center mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 font-sans text-sm text-[#0A1829]/60 border border-[#0A1829]/15 hover:border-[#0A1829]/30 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-sans font-semibold text-sm py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {deleting ? (
                  <><i className="ri-loader-4-line animate-spin text-sm"></i> Excluindo...</>
                ) : (
                  <><i className="ri-delete-bin-line text-sm"></i> Excluir</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
